<template>
  <button
    class="mds-btn"
    :class="classes"
    :type="type"
    :disabled="disabled"
    @click="onClick"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <template v-if="havePrefixIcon">
      <VIcon v-if="material" :material="iconMaterial" theme="outlined"/>
      <slot v-else name="icon"/>
    </template>

    <slot v-if="haveContent"/>

    <template v-if="havePostfixIcon">
      <VIcon v-if="material" :material="iconMaterial" theme="outlined"/>
      <slot v-else name="icon"/>
    </template>
  </button>
</template>

<style lang="scss" src="./VButton.scss"/>

<script>
import Constants from "@/core/misc/Constants.js";
import VIcon from "@/lib/components/VIcon/VIcon.vue"

export default {
  name: "VButton",
  inheritAttrs: true,

  components: {
    VIcon,
  },

  props: {
    /**
     * Расположить иконку после текста.
     */
    iconPostfix: {
      type: Boolean,
      default: false,
    },
    /**
     * Тема кнопки. Варианты `default primary success`
     */
    theme: {
      type: String,
      default: "default",
      validator: (value) => Constants.BUTTON_THEMES.includes(value),
    },
    /**
     * Тип кнопки: button | submit | reset
     */
    type: {
      type: String,
      default: "button",
    },
    /**
     * Контрол включен/выключен
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Поясняющий текст возле кнопки, содержимое тултипа
     */
    title: {
      type: String,
      default: null,
    },
    /**
     * Material-иконка кнопки — шорткат вместо <template #icon><VIcon material="..."/></template>.
     * Имеет приоритет над слотом #icon: если передан material, слот игнорируется.
     */
    material: {
      type: String,
      default: null,
    },
    /**
     * Material-иконка под курсором — подменяет material, пока курсор над кнопкой
     * (в том числе над заблокированной). Работает только вместе с material, на слот #icon не влияет.
     */
    hoverMaterial: {
      type: String,
      default: null,
    },
  },

  emits: ["click"],

  data() {
    return {
      /** Курсор над кнопкой — для подмены иконки на hoverMaterial */
      isHovered: false,
    };
  },

  computed: {
    haveIcon() {
      return !!this.material || !!this.$slots.icon;
    },
    havePrefixIcon() {
      return this.haveIcon && !this.iconPostfix;
    },
    havePostfixIcon() {
      return this.haveIcon && this.iconPostfix;
    },
    haveContent() {
      return !!this.$slots.default;
    },
    /** Иконка с учётом наведения: под курсором — hoverMaterial, если задан */
    iconMaterial() {
      if (this.isHovered === true && this.hoverMaterial !== null) {
        return this.hoverMaterial;
      }
      else {
        return this.material;
      }
    },
    classes() {
      const result = {
        "with-icon": this.haveIcon,
        "prefix-icon": this.haveIcon && this.haveContent && !this.iconPostfix,
        "postfix-icon": this.haveIcon && this.haveContent && this.iconPostfix,
        empty: !this.haveContent,
      };
      result[this.theme] = true;
      return result;
    },
  },

  methods: {
    onClick(event) {
      // Клик может увести со страницы (роутинг) — кнопка размонтируется
      // без mouseleave, и тултип повиснет. Прячем его сразу.
      this.$core.tooltip.hide();

      if (!this.disabled) {
        this.$emit("click", event);
      }
    },

    onMouseEnter() {
      // до проверки title: подмена иконки нужна и у кнопок без тултипа
      this.isHovered = true;

      if (!this.title) return;

      this.$core.tooltip.show(this.$el, this.title);
    },

    onMouseLeave() {
      this.isHovered = false;

      this.$core.tooltip.hide();
    },
  },
};
</script>
